import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import { Vector as VectorLayer } from 'ol/layer';
import { fromLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import { createRoot } from 'react-dom/client';

import Marker from '@/components/ui/map/Marker';
import { STYLES } from '@/define/olMaps';

/**
 * OpenLayers 맵에서 마커와 경로를 그리는 유틸리티 함수들
 */
class DrawingUtils {
  /**
   * 마커 오버레이 생성
   * @param {Function} createOverlay - Overlay 생성 함수
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Object} position - { lat, lng } 좌표
   * @param {string} label - 마커 레이블
   * @param {string} color - 마커 색상
   * @returns {Overlay} 생성된 오버레이
   */
  static drawMarker(createOverlay, map, position, label, color) {
    const element = document.createElement('div');
    // eslint-disable-next-line react/react-in-jsx-scope
    createRoot(element).render(<Marker color={color} data={{ label, position }} />);

    const overlay = createOverlay({
      element,
      position: fromLonLat([position.lng, position.lat]),
      positioning: 'bottom-center',
      insertFirst: false,
    });
    map.addOverlay(overlay);

    return overlay;
  }

  /**
   * 경로 레이어 생성
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Array} paths - 경로 좌표 배열의 배열 [[[lng, lat], [lng, lat]], ...]
   * @param {string} color - 경로 색상
   * @returns {VectorLayer} 생성된 경로 레이어
   */
  static drawPaths(map, paths, color) {
    const features = paths.map((path) => new Feature(new LineString(path)));
    const source = new VectorSource({ features });
    const layer = new VectorLayer({
      source,
      style: STYLES.LINE.PATH(color),
    });

    map.addLayer(layer);
    return layer;
  }

  /**
   * Way 마커들 생성
   * @param {Function} createOverlay - Overlay 생성 함수
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Array} markers - 마커 정보 배열 [{ position, label }, ...]
   * @param {string} color - 마커 색상
   * @returns {Array} 생성된 오버레이 배열
   */
  static drawWayMarkers(createOverlay, map, markers, color) {
    return markers.map(({ position, label }) => {
      return DrawingUtils.drawMarker(createOverlay, map, position, label, color);
    });
  }

  /**
   * Way 경로 생성
   * @param {Object} map - OpenLayers 맵 인스턴스
   * @param {Array} markers - 마커 정보 배열 [{ position }, ...]
   * @param {string} color - 경로 색상
   * @returns {VectorLayer} 생성된 경로 레이어
   */
  static drawWayPaths(map, markers, color) {
    const paths = [];
    markers.slice(1).forEach((currMarker, index) => {
      const prevMarker = markers[index];
      paths.push([
        fromLonLat([prevMarker.position.lng, prevMarker.position.lat]),
        fromLonLat([currMarker.position.lng, currMarker.position.lat]),
      ]);
    });

    return DrawingUtils.drawPaths(map, paths, color);
  }
}

export default DrawingUtils;
